import React from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Close } from '@mui/icons-material';
import { useGetLoginLastWeek } from '../../hooks/reactQuerys/DashboardQueries';

export interface IChartData {
  name: string;
  value: number;
}

interface IProps {
  keyName: string;
  activeDashboard: string[];
  setActiveDashboard: Function;
}

export const PieChartLoggedUser: React.FC<IProps> = ({
  keyName,
  activeDashboard,
  setActiveDashboard,
}) => {
  const { loginsLastWeek, isLoadingLoginLastWeek } = useGetLoginLastWeek();

  const total = loginsLastWeek ? Number(loginsLastWeek.Total) : 0;
  const loggedUser = loginsLastWeek ? Number(loginsLastWeek.LoggedUser) : 0;

  const chartData: IChartData[] = [
    {
      name: 'Angemeldete',
      value: loggedUser,
    },
    {
      name: 'Nicht angemeldete',
      value: total - loggedUser,
    },
  ];

  const hasData = total > 0;

  const COLORS = [
    'rgb(46, 125, 50)', // Angemeldete
    'rgb(211, 47, 47)', // Nicht angemeldete
  ];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = (props: any) => {
    const radius =
      Number(props['innerRadius']) +
      (Number(props['outerRadius']) - Number(props['innerRadius'])) * 0.5;
    const x =
      Number(props['cx']) +
      radius * Math.cos(-Number(props['midAngle']) * RADIAN);
    const y =
      Number(props['cy']) +
      radius * Math.sin(-Number(props['midAngle']) * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill='white'
        textAnchor={x > Number(props['cx']) ? 'start' : 'end'}
        dominantBaseline='central'
      >
        {`${(Number(props['percent']) * 100).toFixed(0)}%`}
      </text>
    );
  };

  const onRemove = () => {
    setActiveDashboard([...activeDashboard.filter(x => x !== keyName)]);
  };

  if (isLoadingLoginLastWeek) {
    return <Skeleton width={300} height={500} sx={{ mt: -14 }} />;
  }

  return (
    <Card variant='outlined'>
      <IconButton sx={{ float: 'right' }} onClick={onRemove}>
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
      <CardContent>
        <Box sx={{ display: 'flex' }}>
          <Typography>Eingeloggte neue Benutzer (letzte 7 Tage)</Typography>
        </Box>
        <Box
          sx={{
            height: { xs: 150, sm: 250, xl: 300 },
            mt: 2,
            width: '100%',
          }}
        >
          {hasData ? (
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='45%' // etwas höher setzen, um Platz für Legende unten zu schaffen
                  labelLine={false}
                  outerRadius='80%'
                  fill='#4cb1ad'
                  dataKey='value'
                  label={renderCustomizedLabel}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend
                  layout='horizontal'
                  verticalAlign='bottom'
                  align='center'
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color='textSecondary' align='center'>
                Keine neuen Benutzer in den letzten 7 Tagen.
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
